import { useCallback, useEffect, useMemo, useState } from 'react';
import { Ban, ExternalLink, Mail, RefreshCw, Send, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api, dateTime, emailAddress } from '../lib/api';
import { Status } from './UI';

const post = body => ({ method:'POST', headers:{'content-type':'application/json'}, body:JSON.stringify(body) });
const purposeLabels = {
  general:'Service invitation',
  private_preview:'Private preview',
  product_testing:'Product testing',
  early_access:'Early access',
};
const terminalStatuses = new Set(['cancelled','expired']);
const shopCode = 'SOUSA_MURRAY_SHOP';

function invitationAccessLabel(invitation){
  const shop=(invitation.services||[]).some(service=>service.service_code===shopCode);
  if(!shop)return null;
  if(invitation.status==='accepted')return {label:'Pre-launch Shop access active',tone:'active'};
  if(invitation.status==='sent')return {label:'Awaiting Shop invitation acceptance',tone:'pending'};
  if(invitation.status==='expired')return {label:'Shop invitation expired',tone:'inactive'};
  if(invitation.status==='cancelled'){
    const revoked=(invitation.events||[]).some(event=>event.event_type==='revoked');
    return {label:revoked?'Pre-launch Shop access revoked':'Shop invitation cancelled',tone:'inactive'};
  }
  if(invitation.status==='delivery_failed')return {label:'Invitation email delivery failed',tone:'warning'};
  return {label:'No active pre-launch Shop access',tone:'inactive'};
}

export default function CustomerInvitationsPanel({customerId,customerNumber}){
  const navigate=useNavigate();
  const[data,setData]=useState({summary:{},invitations:[]});
  const[loading,setLoading]=useState(true);
  const[busyId,setBusyId]=useState('');
  const[error,setError]=useState('');
  const[notice,setNotice]=useState('');

  const load=useCallback(async()=>{
    setLoading(true);
    try{
      const body=await api('/api/customers/'+encodeURIComponent(customerId)+'/service-invitations');
      setData(body||{summary:{},invitations:[]});
      setError('');
    }catch(e){
      setError(e.message);
    }finally{
      setLoading(false);
    }
  },[customerId]);

  useEffect(()=>{void load();},[load]);

  const invitations=data.invitations||[];
  const summary=data.summary||{};
  const metrics=useMemo(()=>[
    ['Invitation records',summary.total||0],
    ['Awaiting acceptance',summary.sent||0],
    ['Accepted',summary.accepted||0],
    ['Cancelled',summary.cancelled||0],
  ],[summary]);

  const act=async(invitation,kind)=>{
    const accepted=invitation.status==='accepted';
    const prompt=kind==='cancel'
      ? accepted
        ? 'Revoke this customer’s accepted invitation access? The invitation and audit history will remain on Customer360.'
        : 'Cancel this invitation? The invitation and audit history will remain on Customer360.'
      : 'Resend this invitation to '+invitation.recipient_email+'?';
    if(!window.confirm(prompt))return;
    setBusyId(invitation.id);setError('');setNotice('');
    try{
      await api('/api/service-invitations/'+encodeURIComponent(invitation.id)+'/'+kind,post({}));
      setNotice(kind==='cancel'?(accepted?'Invitation access revoked.':'Invitation cancelled.'):'Invitation resent.');
      await load();
    }catch(e){
      setError(e.message);
    }finally{
      setBusyId('');
    }
  };

  const openComposer=()=>navigate('/service-invitations?customer='+encodeURIComponent(customerNumber||customerId));

  return <div className="customer-invitations-panel">
    <div className="customer-invitations-toolbar">
      <div>
        <strong>Customer invitation record</strong>
        <span>Current Studio service invitations and historical invitation records remain attached to this Customer360 record.</span>
      </div>
      <div className="customer-invitations-actions">
        <button className="button secondary small govuk-button govuk-button--secondary" type="button" onClick={()=>void load()} disabled={loading}><RefreshCw size={14}/>Refresh</button>
        <button className="button primary small govuk-button" type="button" onClick={openComposer}><Send size={14}/>New invitation</button>
      </div>
    </div>

    {(notice||error)&&<div className={'customer-invitation-message '+(error?'error':'success')}>{error||notice}</div>}

    <div className="customer-invitation-metrics">
      {metrics.map(([label,value])=><div key={label}><span>{label}</span><strong>{value}</strong></div>)}
    </div>

    {loading&&!invitations.length
      ? <div className="customer-invitation-empty"><RefreshCw size={20} className="spin"/><strong>Loading invitations…</strong></div>
      : !invitations.length
        ? <div className="customer-invitation-empty"><Mail size={24}/><strong>No invitations recorded for this customer.</strong><span>Use New invitation to invite this customer to a Sousa Murray service or controlled preview.</span></div>
        : <div className="customer-invitation-list">{invitations.map(invitation=>{
          const access=invitationAccessLabel(invitation);
          const services=invitation.services||[];
          const events=invitation.events||[];
          const busy=busyId===invitation.id;
          const canResend=!terminalStatuses.has(invitation.status)&&invitation.status!=='accepted';
          const canCancel=!terminalStatuses.has(invitation.status);
          return <article className="customer-invitation-card" key={invitation.id}>
            <div className="customer-invitation-card-head">
              <div>
                <span className="customer-invitation-reference">{invitation.invitation_reference}</span>
                <h3 className="govuk-heading-s">{purposeLabels[invitation.purpose]||invitation.purpose||'Service invitation'}</h3>
                <small>Sent to {emailAddress(invitation.recipient_email)}</small>
              </div>
              <Status value={invitation.status||'unknown'}/>
            </div>

            <div className="customer-invitation-services">
              {services.map(service=><span key={service.service_code}>{service.service_name}</span>)}
            </div>

            {access&&<div className={'customer-invitation-access '+access.tone}><ShieldCheck size={15}/><span>{access.label}</span></div>}

            <dl className="customer-invitation-details">
              <div><dt>Sent</dt><dd>{dateTime(invitation.last_sent_at||invitation.sent_at||invitation.created_at)}</dd></div>
              <div><dt>Accepted</dt><dd>{dateTime(invitation.accepted_at)}</dd></div>
              <div><dt>Expires</dt><dd>{invitation.expires_at?dateTime(invitation.expires_at):'No expiry set'}</dd></div>
              <div><dt>Created by</dt><dd>{invitation.created_by_name||'Staff'}</dd></div>
            </dl>

            {invitation.personal_message&&<div className="customer-invitation-personal-message"><strong>Message sent with invitation</strong><p className="govuk-body">{invitation.personal_message}</p></div>}

            <div className="customer-invitation-footer">
              <div className="customer-invitation-row-actions">
                <button className="button secondary small govuk-button govuk-button--secondary" type="button" disabled={!canResend||busy} onClick={()=>void act(invitation,'resend')}><RefreshCw size={14}/>Resend</button>
                <button className="button danger-outline small govuk-button govuk-button--secondary" type="button" disabled={!canCancel||busy} onClick={()=>void act(invitation,'cancel')}><Ban size={14}/>{invitation.status==='accepted'?'Revoke access':'Cancel'}</button>
              </div>
              <button className="button secondary small govuk-button govuk-button--secondary" type="button" onClick={()=>navigate('/service-invitations?customer='+encodeURIComponent(customerNumber||customerId))}><ExternalLink size={14}/>Invitation workspace</button>
            </div>

            {events.length>0&&<details className="customer-invitation-audit">
              <summary>Invitation audit history · {events.length} event{events.length===1?'':'s'}</summary>
              <div>{events.map(event=><div key={event.id}><span><strong>{String(event.event_type||'event').replaceAll('_',' ')}</strong>{event.summary}</span><small>{dateTime(event.created_at)} · {event.actor_name||'System'}</small></div>)}</div>
            </details>}
          </article>;
        })}</div>}
  </div>;
}
